import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { type BaseSyntheticEvent, useEffect, useMemo } from "react";
import {
  type SubmitHandler,
  useForm,
  type UseFormReturn
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useNavigationBlocker,
  usePostGeneratePresignedUrlMutation,
  usePostUploadMediaMutation
} from "@/hooks";
import { useGetProfileQuery } from "@/modules/profile";

import { isPromoAvailableAtom } from "../../atoms/product-form-atom";
import type { ProductCategoryType } from "../../types";
import { getCategoryNameFromId } from "../../utils/form-payload";
import {
  buildCreateProductPayload,
  buildDraftProductPayload,
  buildEditProductPayload
} from "../../utils/payload-builders";
import { useDeleteProductDraftMutation } from "../mutations/use-delete-product-draft-mutation";
import { usePostCreateProductDraftMutation } from "../mutations/use-post-create-product-draft-mutation";
import { usePostCreateProductMutation } from "../mutations/use-post-create-product-mutation";
import { usePostEditProductDraftMutation } from "../mutations/use-post-edit-product-draft-mutation";
import { usePostEditProductMutation } from "../mutations/use-post-edit-product-mutation";
import { useGetProductDetailQuery } from "../queries/use-get-product-detail-query";
import { useGetProductDraftDetailQuery } from "../queries/use-get-product-draft-detail-query";
import {
  type EditProductFormSchema,
  useEditProductFormSchema
} from "../schemas/use-edit-product-form-schema";

const route = getRouteApi("/products/list/$slug/edit");

type NumberStringOrNull = number | string | null;

type ProductVariantApi = {
  id?: number | null;
  quantity?: NumberStringOrNull;
  price?: NumberStringOrNull;
  unit?: string | null;
  provisioning_id?: string | null;
};

type ProductPackageApi = {
  id?: number | null;
  name?: string | null;
  is_free_trial?: boolean | null;
  specifications?: string[];
  variants?: ProductVariantApi[];
};

type UseEditProductFormReturn = {
  formMethods: UseFormReturn<EditProductFormSchema>;
  productCategory: ProductCategoryType | undefined;
  isPending: boolean;
  isLoading: boolean;
  isNotFound: boolean;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  onSaveDraft: () => Promise<void>;
  isSavingDraft: boolean;
  updatedAt: string | undefined;
};

export const useEditProductForm = (): UseEditProductFormReturn => {
  const { slug } = route.useParams();
  const { draft } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("product", {
    keyPrefix: "editProductPage"
  });
  const { t: tProductNoPrefix } = useTranslation("product");
  const { data: profileData } = useGetProfileQuery();
  const setIsPromoAvailable = useSetAtom(isPromoAvailableAtom);

  const companyName = useMemo(() => {
    const name = profileData?.data?.company_name;
    return name ? name : "-";
  }, [profileData]);

  const {
    data: productData,
    isLoading,
    isError: isProductError
  } = useGetProductDetailQuery({
    queryParams: {
      slug
    },
    options: {
      enabled: !draft
    }
  });

  const {
    data: draftData,
    isLoading: draftLoading,
    isError: isDraftError
  } = useGetProductDraftDetailQuery({
    queryParams: {
      id: slug
    },
    options: {
      enabled: draft
    }
  });

  const currentData = draft ? draftData?.data : productData?.data;
  const productCategory =
    currentData?.product_category?.name ||
    getCategoryNameFromId(currentData?.product_category_id);

  const editProductSchema = useEditProductFormSchema(
    productCategory ?? "software"
  );

  const { mutateAsync, isPending } = usePostEditProductMutation();
  const { mutateAsync: mutateCreateDraftAsync, isPending: isCreatingDraft } =
    usePostCreateProductDraftMutation();
  const { mutateAsync: mutateDraftAsync, isPending: isSavingDraft } =
    usePostEditProductDraftMutation();
  const { mutateAsync: createMutateAsync, isPending: isCreating } =
    usePostCreateProductMutation();
  const { mutateAsync: deleteDraftAsync } = useDeleteProductDraftMutation();
  const { mutateAsync: mutateUploadMediaAsync, isPending: isUploadingMedia } =
    usePostUploadMediaMutation();
  const { mutateAsync: mutateGeneratePresignedUrlAsync } =
    usePostGeneratePresignedUrlMutation();

  const { register, control, handleSubmit, ...methods } =
    useForm<EditProductFormSchema>({
      resolver: zodResolver(editProductSchema),
      defaultValues: {
        productId: "-",
        productCode: "",
        productName: "",
        productType: "",
        productService: "",
        version: "-",
        company: companyName,
        productDescription: "",
        specification: "",
        link: "",
        packages: [],
        pictures: [],
        provideTrial: false,
        apiPackage: {
          host: "",
          header: "",
          key: "",
          apis: [
            {
              name: "",
              endpoint: "",
              method: "",
              body: [{ key: "", type: "" }],
              response: ""
            }
          ]
        },
        showOnBuyer: true
      }
    });

  const {
    formState: { isDirty, isValid }
  } = methods;

  useNavigationBlocker(isDirty && !isValid && !isSavingDraft);

  useEffect(() => {
    const loading = draft ? draftLoading : isLoading;

    if (currentData && !loading) {
      methods.reset({
        isRecurring: currentData.is_recurring_available,
        productId: "-",
        productCode: currentData.product_code ?? "",
        productName: currentData.name ?? "",
        productType:
          currentData.product_type_id != null &&
          currentData.product_type_id !== "0"
            ? String(currentData.product_type_id)
            : "",
        productService: currentData.product_service ?? "",
        version: "-",
        company: companyName,
        productDescription: currentData.description ?? "",
        specification: currentData.information ?? "",
        link: currentData.url_sandbox ?? "",
        packages: (currentData.product_package ?? []).map(
          (p: ProductPackageApi) => ({
            packageId: p.id ?? undefined,
            name: p.name ?? "",
            isFreeTrial: p.is_free_trial ?? false,
            specifications:
              p.specifications &&
              Array.isArray(p.specifications) &&
              p.specifications.length > 0
                ? p.specifications
                : [""],
            variants: (p.variants ?? []).map((v: ProductVariantApi) => ({
              variantId: v.id ?? undefined,
              quantity:
                typeof v.quantity === "number"
                  ? v.quantity
                  : Number(v.quantity ?? 0),
              price:
                typeof v.price === "number" ? v.price : Number(v.price ?? 0),
              unit: v.unit ?? "",
              provisioningId: v.provisioning_id ?? ""
            }))
          })
        ),
        provideTrial:
          typeof currentData.is_provide_free_trial === "boolean"
            ? currentData.is_provide_free_trial
            : false,
        tncTrial: currentData.tnc_product_trial ?? "",
        tncCheckout: currentData.tnc_product_checkout ?? "",
        tncRecurring: currentData.tnc_recurring ?? "",
        contactEmail: "-",
        pictures: (currentData.product_photo ?? []).map(
          (photo: any, index: number, arr: any[]) => {
            const hasThumbnail = arr.some((p) => p.type === "thumbnail");
            return {
              id: photo.id,
              mediaPath: photo.media_path,
              type:
                photo.type === "thumbnail" || (!hasThumbnail && index === 0)
                  ? "thumbnail"
                  : "image",
              uid: photo.id
                ? photo.id.toString()
                : Math.random().toString(36).substring(7)
            };
          }
        ),
        apiPackage: currentData.product_api_package
          ? {
              host: currentData.product_api_package.host ?? "",
              header: currentData.product_api_package.header ?? "",
              key: currentData.product_api_package.key ?? "",
              apis: (currentData.product_api_package.apis ?? []).map(
                (api: any) => ({
                  ...api,
                  body: Array.isArray(api.body)
                    ? api.body
                    : [{ key: "", type: "" }]
                })
              )
            }
          : {
              host: "",
              header: "",
              key: "",
              apis: []
            },
        showOnBuyer: currentData.is_show_docs ?? false,
        industryCategory: (currentData.product_industry_category ?? []).map(
          (category) => category.slug_name
        )
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, isLoading, draftLoading, draft]);

  const uploadProductImagesIfNeeded = async () => {
    const pictures = methods.getValues("pictures");
    if (!pictures || pictures.length === 0) return pictures;

    const uploadPromises = pictures.map(async (pic: any) => {
      if (!pic.file) return pic;

      try {
        const presignedRes = await mutateGeneratePresignedUrlAsync({
          mime_type: pic.file.type,
          size: pic.file.size,
          type: "image"
        });

        if (presignedRes?.data?.media_path) {
          const targetPath = presignedRes.data.media_path;

          mutateUploadMediaAsync({
            file: pic.file,
            type: "image",
            object_name: "product",
            object_type: "product",
            reference_no: targetPath.split("/").pop()
          }).catch((err) =>
            console.error("Background product image upload failed:", err)
          );

          return { ...pic, mediaPath: targetPath, file: undefined };
        }
      } catch (err) {
        console.error(
          "Failed to generate presigned URL for product image:",
          err
        );
      }
      return pic;
    });

    const updatedPictures = await Promise.all(uploadPromises);
    methods.setValue("pictures", updatedPictures);
    return updatedPictures;
  };
  const onSaveDraft = async () => {
    try {
      if (!currentData) return;

      const values = methods.getValues();
      const updatedPictures = await uploadProductImagesIfNeeded();

      if (updatedPictures) {
        values.pictures = updatedPictures;
      }

      const draftId = draft ? currentData.key : undefined;

      const payload = buildDraftProductPayload(
        values as any,
        productCategory,
        companyName,
        draftId,
        true
      );

      let res;

      // handle create draft or update draft if draft key is exist
      if ((currentData?.id != null || currentData?.key != null) && draft) {
        res = await mutateDraftAsync(payload as any);
      } else {
        res = await mutateCreateDraftAsync(payload as any);
      }

      if (res && !(res as any).errors) {
        notifications.show({
          message: tProductNoPrefix(
            "addProductPage.notifications.successSaveDraftProduct"
          )
        });
        // Clear dirty state to avoid navigation blocker when leaving the page
        methods.reset(values);
        navigate({
          to: "/products/list",
          search: {
            tab: "draft"
          }
        });
      }
    } catch {
      notifications.show({
        variant: "error",
        message: tProductNoPrefix(
          "addProductPage.notifications.failedToSaveDraft"
        )
      });
    }
  };

  const publishDraft = async (values: EditProductFormSchema) => {
    const updatedPictures = await uploadProductImagesIfNeeded();

    if (updatedPictures) {
      values.pictures = updatedPictures;
    }

    const payload = buildCreateProductPayload(
      values as any,
      productCategory,
      companyName
    );

    const createRes = await createMutateAsync(payload);

    if (createRes && !(createRes as any).errors) {
      try {
        await deleteDraftAsync({ productId: slug });
      } catch {
        // ignore delete draft error; publishing succeeded
      }
      notifications.show({
        message: tProductNoPrefix(
          "addProductPage.notifications.successCreateProduct"
        )
      });
      navigate({ to: "/products/list" });
    }
  };

  const updateProduct = async (values: EditProductFormSchema) => {
    if (!productData?.data) return;

    const updatedPictures = await uploadProductImagesIfNeeded();

    if (updatedPictures) {
      values.pictures = updatedPictures;
    }

    const payload = buildEditProductPayload(
      values as any,
      productCategory,
      companyName,
      productData.data.id,
      productData.data.product_status,
      productData.data.is_publish
    );

    const res = await mutateAsync(payload);

    if (res && !res.errors) {
      notifications.show({
        message: t("notifications.successUpdateProduct")
      });
      navigate({ to: "/products/list" });
    }
  };

  const onSubmit: SubmitHandler<EditProductFormSchema> = async (values) => {
    try {
      if (!slug) return;

      if (draft) {
        await publishDraft(values);
        return;
      }

      if (!productData?.data) return;
      await updateProduct(values);
    } catch {
      notifications.show({
        variant: "error",
        message: draft
          ? tProductNoPrefix(
              "addProductPage.notifications.failedToCreateProduct"
            )
          : t("notifications.failedToUpdateProduct")
      });
    }
  };

  const formMethods: UseFormReturn<EditProductFormSchema> = {
    ...methods,
    handleSubmit,
    register,
    control
  };

  const isCurrentLoading = draft ? draftLoading : isLoading;
  const isNotFound = draft
    ? !draftLoading && (isDraftError || !draftData?.data)
    : !isLoading && (isProductError || !productData?.data);

  useEffect(() => {
    setIsPromoAvailable(productData?.data?.is_promo_available ?? false);
  }, [productData, setIsPromoAvailable]);

  return {
    formMethods,
    productCategory,
    isPending: isPending || isCreating || isUploadingMedia,
    isLoading: isCurrentLoading,
    isNotFound,
    onSubmit: handleSubmit(onSubmit),
    onSaveDraft,
    isSavingDraft: isSavingDraft || isCreatingDraft || isUploadingMedia,
    updatedAt: productData?.data?.updated_at
  };
};
